<template>
  <artmisads-modal
    :open="open"
    :title="t('seller.accBonusProgram.campaignsTitle')"
    :width="1000"
    :maskClosable="false"
    :footer="null"
    :onCancel="() => (open = false)"
    :destroyOnClose="true"
  >
    <artmisads-table
      v-model:pagination="pagination"
      :columns="columns"
      :data-source="dataSource"
      :change="onTableChange"
      :loading="load"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'campaignStatus'">
          <Status :type="campaignStatusType(record.campaignStatus)" :text="statusText(record.campaignStatus)" />
        </template>
        <template v-if="column.key === 'dateRange'">
          {{ formatDate(record.startDateTimeInEpochMills) }} - {{ formatDate(record.scheduledEndDateTimeInEpochMills) }}
        </template>
        <template v-if="column.key === 'budget' || column.key === 'budgetRemaining'">
          {{ currencySymbol }} {{ formatAmount(record[column.key]) }}
        </template>
      </template>
    </artmisads-table>
  </artmisads-modal>
</template>

<script setup>
import { ref, reactive, computed, defineExpose } from 'vue';
import { useI18n } from 'vue-i18n';
import Status from '~/components/Status.vue';
import { accBonusProgramApi } from '@/server/index';
import { CURRENCY_DATA_MAPPER } from '@/contants/common';

import moment from 'moment';

const { t } = useI18n();
const open = ref(false);
const load = ref(false);
const dataSource = ref([]);
const programId = ref('');
const currencyCode = ref('USD');

const currencySymbol = computed(() => CURRENCY_DATA_MAPPER[currencyCode.value] || '$');

const pagination = reactive({
  total: 0,
  current: 1,
  size: 10,
  pageSize: 10,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50'],
});

const columns = computed(() => [
  { title: t('seller.accBonusProgram.colName'), dataIndex: 'campaignName', key: 'campaignName' },
  { title: t('seller.common.brand'), dataIndex: 'brandName', key: 'brandName' },
  { title: t('seller.common.status'), dataIndex: 'campaignStatus', key: 'campaignStatus' },
  { title: t('seller.accBonusProgram.colDateRange'), dataIndex: 'dateRange', key: 'dateRange', width: 200 },
  { title: t('seller.accBonusProgram.colBudget'), dataIndex: 'budget', key: 'budget', width: 150 },
  {
    title: t('seller.accBonusProgram.colBudgetRemaining'),
    dataIndex: 'budgetRemaining',
    key: 'budgetRemaining',
    width: 150
  },
]);

const formatDate = (d) => (d ? moment(d).format('YYYY-MM-DD') : '--');
const formatAmount = (val) => Number(val || 0).toFixed(2);

const statusText = (status) => {
  const map = {
    DELIVERING: t('seller.sellerAccount.campaignStatusDelivering'),
    ENDED: t('seller.sellerAccount.campaignStatusEnded'),
    EXPIRED: t('seller.sellerAccount.campaignStatusExpired'),
    OUTOFBUDGET: t('seller.sellerAccount.campaignStatusOutOfBudget'),
    CREATED: t('seller.sellerAccount.campaignStatusCreated'),
    DELETED: t('seller.sellerAccount.campaignStatusDeleted'),
  };
  return map[status] || status;
};

const campaignStatusType = (status) => {
  if (status === "ACTIVE" || status === "SCHEDULED" || status === "ENDED") return "active"
  if (status === "EXPIRED") return "finished"
  if (status === "OUT_OF_BUDGET") return "pending"
  if (status === "ENDED_GRACE_PERIOD") return "inactive"
  if (status === "DELETED") return "not_authorized"
  if (status === "DELIVERING" || status === "CREATED") return "delivering"
  return "inactive"
}

const onTableChange = ({ current, pageSize }) => {
  pagination.current = current;
  pagination.size = pageSize;
  pagination.pageSize = pageSize;
  fetchList();
};

const fetchList = async () => {
  if (!programId.value) return;
  load.value = true;
  try {
    const res = await accBonusProgramApi.getProgramCampaignDetail({
      programId: programId.value,
      page: pagination.current,
      size: pagination.size,
    });
    if (res?.success && res?.data) {
      console.log(res.data);
      const list = res.data.data ?? res.data;
      dataSource.value = list;
      pagination.total = res.data.total ?? list.length;
    } 
  } catch {
    // dataSource.value = MOCK_CAMPAIGNS;
    // pagination.total = MOCK_CAMPAIGNS.length;
  } finally {
    load.value = false;
  }
};

const show = (row) => {
  programId.value = row.programId;
  currencyCode.value = row.currency || 'USD';
  pagination.current = 1;
  open.value = true;
  fetchList();
};

defineExpose({ show });
</script>
